// scheduling/utils/capacity.js
// Single source of truth for staff weekly capacity / availability.
//
// These functions are lifted verbatim from SchedulerGrid.jsx (the one place the
// capacity math was historically correct) so every availability surface — the
// grid, the Staff Suggestions heatmap, the placeholder-assign modal, the
// EmployeeProfile + StaffDashboard utilization views — computes capacity the
// SAME way and can never diverge.
//
// Capacity is PTO-aware (approved PTO reduces a day's cap), holiday-aware
// (closed/partial-cap days), and CalendarTemplate/override-aware (per-staff
// overrides > Default+dept-global pool, with Default-wins-tie). Scheduled hours
// include global-assignment hours so they count toward utilization.
//
// Holidays come from the admin-editable rprs.Holidays table (passed in as the
// `holidays` rows). Those rows store recurrence RULES — fixed Month/Day, or a
// floating PatternRule like FOURTH_THU/Nov — plus each row's CapacityHours.
// computeHolidayDates() only RESOLVES those stored rules into concrete dates for
// a given year (needed because the heatmap/grid render arbitrary future years);
// it does not hardcode which holidays exist or their caps.

// Local-date ISO (YYYY-MM-DD). Uses local getters, so callers building Date
// objects should use a safe local hour (e.g. T12:00:00) to avoid UTC day shifts.
export function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Holiday date computation ────────────────────────────────────────────────
// Resolves a stored rprs.Holidays PatternRule (+ PatternMonth) to a date in `year`.
export function computePatternDate(rule, month, year) {
  const m = month - 1;
  if (rule === 'LAST_MON') { const d = new Date(year, m + 1, 0); while (d.getDay() !== 1) d.setDate(d.getDate() - 1); return d; }
  if (rule === 'FIRST_MON') { const d = new Date(year, m, 1); while (d.getDay() !== 1) d.setDate(d.getDate() + 1); return d; }
  if (rule === 'SECOND_MON') { let c = 0; const d = new Date(year, m, 1); while (c < 2) { if (d.getDay() === 1) c++; if (c < 2) d.setDate(d.getDate() + 1); } return d; }
  if (rule === 'THIRD_MON') { let c = 0; const d = new Date(year, m, 1); while (c < 3) { if (d.getDay() === 1) c++; if (c < 3) d.setDate(d.getDate() + 1); } return d; }
  if (rule === 'FOURTH_THU') { let c = 0; const d = new Date(year, m, 1); while (c < 4) { if (d.getDay() === 4) c++; if (c < 4) d.setDate(d.getDate() + 1); } return d; }
  if (rule === 'DAY_AFTER_FOURTH_THU') { let c = 0; const d = new Date(year, m, 1); while (c < 4) { if (d.getDay() === 4) c++; if (c < 4) d.setDate(d.getDate() + 1); } d.setDate(d.getDate() + 1); return d; }
  // Mon/Tue/Wed before the 4th Thursday of November (Thanksgiving week) = 4th-Thu minus 3/2/1 days.
  if (rule === 'MON_BEFORE_FOURTH_THU' || rule === 'TUE_BEFORE_FOURTH_THU' || rule === 'WED_BEFORE_FOURTH_THU') {
    let c = 0; const d = new Date(year, m, 1); while (c < 4) { if (d.getDay() === 4) c++; if (c < 4) d.setDate(d.getDate() + 1); }
    const offset = rule === 'MON_BEFORE_FOURTH_THU' ? 3 : rule === 'TUE_BEFORE_FOURTH_THU' ? 2 : 1;
    d.setDate(d.getDate() - offset); return d;
  }
  return new Date(year, m, 1);
}

// Map rprs.Holidays rows → concrete dates for `year`. 'date' holidays nudge off
// weekends (Sat→Fri, Sun→Mon); 'pattern' holidays resolve via computePatternDate.
export function computeHolidayDates(holidays, year) {
  return (holidays || []).map(h => {
    let date;
    if (h.HolidayType === 'date') {
      date = new Date(year, (h.Month || 1) - 1, h.Day || 1);
      if (date.getDay() === 6) date.setDate(date.getDate() - 1);
      if (date.getDay() === 0) date.setDate(date.getDate() + 1);
    } else {
      date = computePatternDate(h.PatternRule, h.PatternMonth, year);
    }
    return { name: h.Name, iso: toISO(date), capHours: h.CapacityHours ?? 0 };
  });
}

// dayOfWeek (0=Sun…6=Sat) → CalendarTemplate per-day hours column.
export const DAY_CAP_KEYS = ['SunHours', 'MonHours', 'TueHours', 'WedHours', 'ThuHours', 'FriHours', 'SatHours'];

// Whether a CalendarTemplate is active for the date `dateISO` (the rendered day —
// callers pass the day being evaluated, NOT today). This is the single chokepoint
// for both absolute and recurring-annual templates.
//   - RecurringAnnually: EffectiveStart/End are matched by month-day only (year
//     ignored), so e.g. busy-season Jan 15–Apr 15 applies every year. Wrap-aware
//     for seasons that cross the new year (start MM-DD > end MM-DD, e.g. Dec–Feb).
//   - Otherwise: EffectiveStart/End are absolute calendar dates.
// A template with neither date is year-round.
export function inRange(t, dateISO) {
  if (!t.IsActive) return false;
  const start = t.EffectiveStart ? String(t.EffectiveStart).slice(0, 10) : null;
  const end = t.EffectiveEnd ? String(t.EffectiveEnd).slice(0, 10) : null;
  if (t.RecurringAnnually && (start || end)) {
    const wk = dateISO.slice(5);                       // 'MM-DD'
    const s = start ? start.slice(5) : null;
    const e = end ? end.slice(5) : null;
    if (s && e) return s <= e ? (wk >= s && wk <= e) : (wk >= s || wk <= e);
    if (s) return wk >= s;
    return wk <= e;
  }
  if (start && dateISO < start) return false;
  if (end && dateISO > end) return false;
  return true;
}

// ── Input builders (from raw schedData fetches) ──────────────────────────────

// PTO hours by staff by day — approved only (affects capacity + conflicts).
// { staffId: { 'YYYY-MM-DD': hours } }
export function buildPtoByStaffDay(ptoData) {
  const map = {};
  for (const p of (ptoData || [])) {
    if (p.Status !== 'approved') continue;
    const d = (typeof p.PTODate === 'string' ? p.PTODate : '').slice(0, 10);
    if (!d) continue;
    if (!map[p.StaffId]) map[p.StaffId] = {};
    map[p.StaffId][d] = (map[p.StaffId][d] || 0) + (p.Hours || 0);
  }
  return map;
}

// Holiday lookup by date, computed from rprs.Holidays rows across the given years.
// `years` is any iterable of full-year numbers covering the window.
// { 'YYYY-MM-DD': { name, capHours } }
export function buildHolidayByDate(holidays, years) {
  const map = {};
  if (!holidays || !holidays.length || !years) return map;
  for (const year of years) {
    for (const h of computeHolidayDates(holidays, year)) {
      map[h.iso] = { name: h.name, capHours: h.capHours };
    }
  }
  return map;
}

// Per-day total scheduled hours by staff. Includes global-assignment hours
// (dept-filtered) so they count toward capacity/conflicts.
// { staffId: { 'YYYY-MM-DD': totalHours } }
export function buildDayHoursByStaff(entries, globalAssignments, staff) {
  const map = {};
  for (const e of (entries || [])) {
    const d = (typeof e.EntryDate === 'string' ? e.EntryDate : '').slice(0, 10);
    if (!d) continue;
    if (!map[e.StaffId]) map[e.StaffId] = {};
    map[e.StaffId][d] = (map[e.StaffId][d] || 0) + (e.Hours || 0);
  }
  for (const ga of (globalAssignments || [])) {
    const iso = typeof ga.AssignmentDate === 'string' ? ga.AssignmentDate.slice(0, 10) : '';
    if (!iso) continue;
    const depts = ga.Departments ? ga.Departments.split(',') : null;
    for (const person of (staff || [])) {
      if (!person.IsActive) continue;
      if (depts && !depts.includes(person.DepartmentCode)) continue;
      if (!map[person.StaffId]) map[person.StaffId] = {};
      map[person.StaffId][iso] = (map[person.StaffId][iso] || 0) + (ga.Hours || 0);
    }
  }
  return map;
}

// ── Employment window ────────────────────────────────────────────────────────
// Is this staffer employed on dateISO? StartDate/EndDate are synced from PE
// (tblStaff.StaffStarted / StaffEnded) by peSync and were previously unused by capacity,
// which is why 11 not-yet-started staff showed a full 40h week six weeks before their
// start date — 10 of them already had work booked against it.
//
// EndDate is honoured ONLY when it is on/after StartDate. Returning interns keep the
// StaffEnded of their PRIOR stint alongside a new StaffStarted (Holland Hensley today:
// start 2026-09-21, end 2026-03-06), so an unguarded `date > EndDate` check would pin
// them at 0h capacity forever.
//
// Dates arrive here as JSON strings from schedData, so slicing is correct. (The
// server-side api/shared/capacityResolve.js reads the same columns as mssql Date objects
// and must use UTC getters instead — same calendar day, different access path.)
export function isEmployedOn(person, dateISO) {
  if (!person) return true;
  const start = person.StartDate ? String(person.StartDate).slice(0, 10) : null;
  const end = person.EndDate ? String(person.EndDate).slice(0, 10) : null;
  if (start && dateISO < start) return false;
  if (end && (!start || end >= start) && dateISO > end) return false;
  return true;
}

// ── Contract vs seasonal templates ───────────────────────────────────────────
// Every baseline row (PE-mirrored contracts, the firm FT/PT/Leave globals, per-staff
// customs) sits at Priority 0. Anything above 0 is a seasonal layer on top of the
// contract — today the two RPRS-owned busy-season dept globals at P=10.
const isSeasonal = (t) => (t.Priority || 0) > 0;
// A seasonal GLOBAL (dept-scoped or firm-wide) — the kind that only full-time staff get.
const isSeasonalUplift = (t) => t.StaffId == null && isSeasonal(t);

const WEEK_KEYS = ['MonHours', 'TueHours', 'WedHours', 'ThuHours', 'FriHours', 'SatHours', 'SunHours'];
export function weeklyHours(t) {
  return t ? WEEK_KEYS.reduce((sum, k) => sum + (Number(t[k]) || 0), 0) : 0;
}

// A "full week" is whatever the firm default template holds (Full Time = 40h), so the
// threshold moves with the firm rather than being a hardcoded 40.
function fullTimeWeeklyHours(templates, firmDefaultId) {
  const firm = (templates || []).find(t => t.TemplateId === firmDefaultId)
    || (templates || []).find(t => t.IsDefault && t.StaffId == null);
  return weeklyHours(firm) || 40;
}

// The staffer's CONTRACT on a date — their P=0 template, ignoring any seasonal layer —
// plus the seasonal templates that will raise it. This is what the grid row label, the
// Capacity report and the schedule chooser show, so nobody has to infer "part-time"
// from a capacity number. Returns null when nothing resolves (the 8h fallback case).
//   { template, name, weeklyHours, kind: 'FT'|'PT'|'Flex'|'Leave', label, fullTime,
//     seasonal: [{ name, weeklyHours, start: 'MM-DD', end: 'MM-DD' }] }
export function resolveContract({ person, templates, dateISO, firmDefaultId }) {
  if (!person) return null;
  const cals = templates || [];
  const base = resolveEffectiveTemplate({
    person, templates: cals.filter(t => !isSeasonal(t)), dateISO, firmDefaultId,
  }).template;
  if (!base) return null;
  const hours = weeklyHours(base);
  const fullTime = hours >= fullTimeWeeklyHours(cals, firmDefaultId);
  const kind = /flex/i.test(base.TemplateName || '') ? 'Flex'
    : hours <= 0 ? 'Leave' : fullTime ? 'FT' : 'PT';
  const h = Math.round(hours * 100) / 100;
  // Seasonal layers that apply to this person in some part of the year — date is
  // deliberately ignored so a November label can still say "50h Jan 15–Apr 15".
  const seasonal = cals.filter(t => t.IsActive && isSeasonal(t) && (
    t.StaffId != null
      ? t.StaffId === person.StaffId
      : fullTime && (!t.DepartmentCode || t.DepartmentCode === person.DepartmentCode)
  )).map(t => ({
    name: t.TemplateName, weeklyHours: weeklyHours(t),
    start: t.EffectiveStart ? String(t.EffectiveStart).slice(5, 10) : null,
    end: t.EffectiveEnd ? String(t.EffectiveEnd).slice(5, 10) : null,
  }));
  return {
    template: base, name: base.TemplateName, weeklyHours: h, kind, fullTime, seasonal,
    label: kind === 'Leave' ? 'Leave' : `${kind} ${h}h`,
  };
}

// ── Template selection ───────────────────────────────────────────────────────
// THE one place that decides which CalendarTemplate applies to a staffer on a date.
// Every surface must call this — the grid (via makeGetDayCap), the EmployeeProfile
// "IN EFFECT" badge, and the Capacity Report. Three separate implementations of this
// used to disagree: the badge tie-broke on TemplateId DESC while getDayCap relied on
// the stable sort preserving queryCalendars' TemplateId ASC order, so the profile page
// could claim 6h/day while the grid scheduled against 8h/day for the same person.
//
// ONE pool, sorted by Priority. Per-staff overrides no longer short-circuit: they
// compete like everything else. That is what lets a Priority-10 busy-season dept global
// out-rank a Priority-0 per-staff baseline — previously ANY override made a staffer
// permanently immune to seasonal uplift (13 AUDIT/TAX part-timers were silently
// affected), and the PE sync writes overrides for every drifted staffer, so it would
// have spread.
//
// Returns { template, pool, ambiguous }. `ambiguous` flags >1 candidate tied at the top
// priority AND at the same override/global rank — i.e. the winner would depend on row
// order. It resolves deterministically now, but it still means someone has stacked
// conflicting rows and should be told.
export function resolveEffectiveTemplate({ person, templates, dateISO, firmDefaultId }) {
  if (!person) return { template: null, pool: [], ambiguous: false };
  const cals = templates || [];

  // The staffer's assigned default only serves as the tiebreak if it actually APPLIES on
  // this date. If it's out of range (a date-bounded template before its start or after its
  // end), fall back to the firm default.
  //
  // Without this, an out-of-range default is catastrophic rather than merely ignored: the
  // "Default wins on tie" branch below matches nothing, so the last-resort TemplateId DESC
  // picks the highest-id P=0 global — which is "Leave" at 0h. A staffer whose default is a
  // future-dated or expired template would silently read 0h capacity. Phase 2's reconcile
  // creates date-bounded templates and points defaults at them, so this would fire every
  // time one of those ranges ended.
  const assignedId = person.DefaultCalendarTemplateId || null;
  const assigned = assignedId ? cals.find(t => t.TemplateId === assignedId) : null;
  const effectiveDefaultId = (assigned && inRange(assigned, dateISO)) ? assignedId : firmDefaultId;

  const pool = [];
  const uplifts = [];
  for (const t of cals) {
    if (!inRange(t, dateISO)) continue;              // covers IsActive + date window
    if (t.StaffId != null) {
      if (t.StaffId !== person.StaffId) continue;    // someone else's override
    } else if (t.DepartmentCode && t.DepartmentCode !== person.DepartmentCode) {
      continue;                                       // dept-scoped global, wrong dept
    }
    (isSeasonalUplift(t) ? uplifts : pool).push(t);
  }

  const rank = (t) => (t.StaffId != null ? 0 : 1);   // per-staff outranks global on a tie
  const byPrecedence = (a, b) => {
    const pd = (b.Priority || 0) - (a.Priority || 0);
    if (pd !== 0) return pd;
    const rd = rank(a) - rank(b);
    if (rd !== 0) return rd;
    // Among globals, the staffer's own Default wins. Without this, the firm-wide P=0
    // globals (Full Time / PT 20 / PT 30 / Leave) tie and "Leave" — highest TemplateId,
    // 0h — would win, zeroing out every full-time staffer. That regression happened once
    // already, right after the 2026-05-18 PE import.
    if (a.TemplateId === effectiveDefaultId) return -1;
    if (b.TemplateId === effectiveDefaultId) return 1;
    return (b.TemplateId || 0) - (a.TemplateId || 0); // newest explicit row wins
  };

  // Seasonal uplift is for FULL-TIME staff only. A busy-season global would otherwise
  // out-rank every P=0 baseline in the dept — Madison May, 2026-11: "part-time people
  // who aren't actually working 50 hours" — 22 TAX/AUDIT part-time, flex and Leave staff
  // read 50h Jan 15–Apr 15. So resolve the contract (P=0) first, and only let the uplift
  // compete when that contract is a full week. A part-timer who genuinely flexes up gets
  // a per-staff busy-season template instead (not a global, so not gated).
  if (uplifts.length) {
    const contract = pool.filter(t => !isSeasonal(t)).sort(byPrecedence)[0];
    if (!contract || weeklyHours(contract) >= fullTimeWeeklyHours(cals, firmDefaultId)) pool.push(...uplifts);
  }
  if (!pool.length) return { template: null, pool, ambiguous: false };

  pool.sort(byPrecedence);

  const top = pool[0];
  // Ambiguity means "someone stacked conflicting rows on ONE PERSON", not merely "there
  // was a tie". Ties among globals are normal and by design — the firm keeps four P=0
  // globals (Full Time / PT 20 / PT 30 / Leave) and the Default rule picks between them,
  // so flagging those would light up every staffer. Two ACTIVE per-staff overrides at the
  // same priority is different: one of them is dead weight and someone should retire it.
  const topPriority = top.Priority || 0;
  const tiedOverrides = pool.filter(t => t.StaffId != null && (t.Priority || 0) === topPriority);
  const ambiguous = rank(top) === 0 && tiedOverrides.length > 1;

  return { template: top, pool, ambiguous };
}

// ── Per-day capacity ─────────────────────────────────────────────────────────
// Build a (staffId, dateISO) => capacity-hours function. Precedence:
//   0. Outside [StartDate, EndDate] → 0h (not employed yet / no longer employed)
//   1. Holiday on that date → holiday.capHours - PTO
//   2. resolveEffectiveTemplate() — one Priority-sorted pool of per-staff overrides
//      + applicable globals → that template's hours for this weekday, minus PTO
//   3. 8h fallback
export function makeGetDayCap({ staff, globalCalendars, ptoByStaffDay, holidayByDate }) {
  const staffArr = staff || [];
  const cals = globalCalendars || [];
  const pto = ptoByStaffDay || {};
  const hols = holidayByDate || {};
  // The firm default global (e.g. "Full Time") — the effective default for any staffer
  // with no DefaultCalendarTemplateId set (e.g. freshly-synced/merger hires), so they
  // baseline to Full Time instead of whatever P=0 global has the highest id.
  const firmDefaultId = cals.find(t => t.IsDefault && t.StaffId == null)?.TemplateId;

  // The grid calls this once per (staffer, day) across a multi-week window, so both of
  // these matter: an O(1) staff lookup instead of a .find() scan, and a memo so the
  // pool build/sort runs once per (staffer, day) rather than per caller.
  const byId = new Map(staffArr.map(s => [s.StaffId, s]));
  const memo = new Map();

  return (staffId, dateISO) => {
    const person = byId.get(staffId);
    if (!person) return 8;

    // 0) Not employed on this date — no template, no holiday, no PTO maths.
    if (!isEmployedOn(person, dateISO)) return 0;

    const ptoHours = pto[staffId]?.[dateISO] || 0;

    // 1) Holiday wins over any template.
    const holiday = hols[dateISO];
    if (holiday) return Math.max(0, (holiday.capHours || 0) - ptoHours);

    // 2) One selection point, memoised per (staffer, day).
    const key = `${staffId}|${dateISO}`;
    let tpl = memo.get(key);
    if (tpl === undefined) {
      tpl = resolveEffectiveTemplate({ person, templates: cals, dateISO, firmDefaultId }).template;
      memo.set(key, tpl);
    }
    if (!tpl) return 8;

    const dayOfWeek = new Date(dateISO + 'T12:00:00').getDay(); // 0=Sun...6=Sat
    return Math.max(0, (Number(tpl[DAY_CAP_KEYS[dayOfWeek]]) || 0) - ptoHours);
  };
}

// Build a (staffId, dateISO?) => resolveContract() lookup over the same staff + templates
// the grid feeds makeGetDayCap, memoised per (staffer, day). dateISO defaults to today.
export function makeGetContract({ staff, globalCalendars }) {
  const cals = globalCalendars || [];
  const firmDefaultId = cals.find(t => t.IsDefault && t.StaffId == null)?.TemplateId;
  const byId = new Map((staff || []).map(s => [s.StaffId, s]));
  const memo = new Map();
  return (staffId, dateISO = toISO(new Date())) => {
    const key = `${staffId}|${dateISO}`;
    if (!memo.has(key)) {
      memo.set(key, resolveContract({ person: byId.get(staffId), templates: cals, dateISO, firmDefaultId }));
    }
    return memo.get(key);
  };
}

// ── Weekly utilization ───────────────────────────────────────────────────────

// The 7 day-ISO strings of the week starting at `mondayISO`.
export function weekDayISOs(mondayISO) {
  const base = new Date(mondayISO + 'T12:00:00');
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(base);
    d.setDate(d.getDate() + i);
    out.push(toISO(d));
  }
  return out;
}

// Compute per-staff, per-week { weekStart, capacity, scheduled, pct } for the
// given Monday-ISO weeks. `getDayCap` from makeGetDayCap; `dayHoursByStaff` from
// buildDayHoursByStaff. pct = 100 when capacity is 0 (PTO/Leave week = unavailable).
export function computeStaffWeeklyUtil({ staffIds, weekMondays, getDayCap, dayHoursByStaff }) {
  const dh = dayHoursByStaff || {};
  return (staffIds || []).map(sid => {
    const staffDayHrs = dh[sid] || {};
    const weeks = (weekMondays || []).map(mon => {
      let scheduled = 0, capacity = 0;
      for (const iso of weekDayISOs(mon)) {
        scheduled += staffDayHrs[iso] || 0;
        capacity += getDayCap(sid, iso);
      }
      scheduled = Math.round(scheduled * 100) / 100;
      capacity = Math.round(capacity * 100) / 100;
      const pct = capacity <= 0 ? 100 : Math.round((scheduled / capacity) * 100);
      return { weekStart: mon, capacity, scheduled, pct };
    });
    return { staffId: sid, weeks };
  });
}
